import React, { useState } from "react";
import { Button } from "rsuite";
import { Pencil, Check, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Col, Row } from "react-bootstrap";
import { saveInfoItem, saveMultiInfoItem } from "../Actions";

/**
 * EditableInfo Component - Unified editable field with auto-save
 *
 * @param {string} title - Field title
 * @param {string} caption - Field description
 * @param {any} value - Current display value
 * @param {ReactNode} icon - Icon to display
 * @param {ReactNode} children - Input/Select component for editing
 * @param {string} fieldKey - Key for backend (e.g., "name", "description")
 * @param {string} productId - Product ID for save operation
 * @param {function} onSaveSuccess - Callback after successful save
 * @param {function} onSaveError - Callback on save error
 */
const EditableInfo = ({
  title,
  caption,
  value,
  icon,
  children,
  fieldKey,
  productId,
  onSaveSuccess = () => {},
  onSaveError = () => {},
  fieldType = "dynamic-list",
  modelId,
}) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editValue, setEditValue] = useState(value || "");

  // Handle edit toggle
  // console.log(value)
  const handleToggle = () => {
    if (!editing) {
      setEditValue(value || "");
      setError(null);
    }
    setEditing((prev) => !prev);
  };

  // Handle input change
  const handleInputChange = (newValue) => {
    setEditValue(newValue);
    setError(null);
  };

  const saveMulti = async () => {
    //   const item = editValue.find((f) => f.type === "update");

    const itemUpdate = editValue
      .filter(function (f) {
        return (
          f.type === "update" &&
          f.editing === true &&
          f.value?.toString().trim() !== ""
        );
      })
      .map(function (f) {
        return { id: f.id, value: f.value };
      });

    const itemNew = editValue
      .filter(function (f) {
        return (
          f.type === "new" &&
          f.editing === true &&
          f.value?.toString().trim() !== ""
        );
      })
      .map(function (f) {
        return { value: f.value };
      });

    setIsSaving(true);
    try {
      if (itemNew.length + itemUpdate.length > 0) {
        await saveMultiInfoItem(productId, fieldKey, itemUpdate, itemNew);
      }
      setEditing(false);
      setError(null);
      onSaveSuccess(editValue);
    } catch (err) {
      console.error("❌ Failed to save:", err);
      setError(err.message || t("error_saving_field") || "Failed to save");
      onSaveError(err);
    } finally {
      setIsSaving(false);
    }
  };
  // Save to backend
  const handleSave = async () => {
    if (typeof editValue === "object") {
      return await saveMulti();
    }

    if (!editValue?.toString().trim() && editValue !== 0) {
      setError(t("field_required") || "This field is required");
      return;
    }

    setIsSaving(true);
    try {
      await saveInfoItem(productId, fieldKey, modelId, editValue);
      setEditing(false);
      setError(null);
      onSaveSuccess(editValue);
    } catch (err) {
      console.error("❌ Failed to save:", err);
      setError(err.message || t("error_saving_field") || "Failed to save");
      onSaveError(err);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setEditValue(value || "");
    setError(null);
    setEditing(false);
  };

  return (
    <div className="editable-info w-100 p-3 mb-3">
      <Row className="d-flex gap-2 flex-column flex-md-row align-items-start align-items-md-center justify-content-between">
        {/* LEFT: Icon + Title + Caption */}
        <Col
          md="12"
          className="d-flex align-items-flex-start gap-3 mb-3 mb-md-0"
        >
          {icon && <div className="text-primary flex-shrink-0">{icon}</div>}
          <div className="flex-grow-1">
            <h6 className="fw-semibold fs-5 mb-2">{title}</h6>
            <div className="p-3">
              {caption && <p className="text-muted fs-7 mb-0">{caption}</p>}
            </div>
          </div>
        </Col>

        {/* RIGHT: Display Value or Input */}
        <Col md="12">
          {editing ? (
            <div className="d-flex flex-column gap-3">
              {React.cloneElement(children, {
                value: editValue,
                onChange: (e) => handleInputChange(e?.target?.value ?? e),
              })}
              {error && (
                <div className="d-flex align-items-center gap-2 text-danger fs-7">
                  <AlertCircle size={14} />
                  <span>{error}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="fs-6 text-dark-purple mx-3">
              {React.isValidElement(value) ? (
                value
              ) : (
                <p className="fw-medium mb-0 text-break">
                  {value?.toString().trim() || "—"}
                </p>
              )}
            </div>
          )}
        </Col>
      </Row>

      {/* BOTTOM: Action Buttons */}
      <div className="d-flex justify-content-end gap-2 mt-3">
        {editing && (
          <Button
            appearance="ghost"
            className="p-2"
            style={{
              color: "#6c757d",
              borderColor: "#6c757d",
            }}
            onClick={handleCancel}
            disabled={isSaving}
          >
            {t("cancel") || "Cancel"}
          </Button>
        )}

        <Button
          appearance={editing ? "primary" : "ghost"}
          className="p-2 custom-btn"
          style={{
            backgroundColor: editing ? "#6610f2" : "transparent",
            color: editing ? "#fff" : "#6610f2",
            borderColor: "#6610f2",
          }}
          onClick={editing ? handleSave : handleToggle}
          disabled={isSaving}
          loading={isSaving}
          startIcon={editing ? <Check size={14} /> : <Pencil size={14} />}
        >
          {isSaving
            ? t("saving") || "Saving..."
            : editing
            ? t("save")
            : t("edit")}
        </Button>
      </div>
    </div>
  );
};

export default EditableInfo;
